import classNames from 'classnames';
import React, { useId, useState } from 'react';
import type { LinkProps } from 'react-router';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { ButtonWithLoadingState } from './Button';
import Buttons from './Buttons';
import * as styles from './Modal.module.scss';
import ModalPrimitive from './ModalPrimitive';
import Tooltip from './Tooltip';

export const ModalFooterContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => <div className={styles.footerContainer}>{children}</div>;

export type ModalFooterButtonBaseProps = {
  text: string;
  disabled?: boolean;
  danger?: boolean;
};

export type ModalFooterSecondaryButtonProps = ModalFooterButtonBaseProps & {
  onClick: () => void;
  tooltip?: string;
};

export type ModalFooterLeftButtonProps = ModalFooterSecondaryButtonProps & {
  variant?: 'link' | 'secondary';
};

export const ModalButton = ({
  text,
  onClick,
  tooltip,
  ...props
}: ModalFooterSecondaryButtonProps | ModalFooterLeftButtonProps) => (
  <Tooltip content={tooltip}>
    <ModalPrimitive.Button {...props} onClick={onClick}>
      {text}
    </ModalPrimitive.Button>
  </Tooltip>
);

export type ModalFooterPrimaryButton = ModalFooterButtonBaseProps &
  (
    | {
        to: LinkProps['to'];
        onClick?: never;
      }
    | {
        to?: never;
        onClick: () => Promise<unknown> | void;
        loading?: boolean;
      }
  );

interface ModalFooterProps {
  primaryButton: ModalFooterPrimaryButton;
  secondaryButton?: ModalFooterSecondaryButtonProps;
  leftButton?: ModalFooterLeftButtonProps;
}

const ModalFooter = ({
  primaryButton: { text: primaryButtonText, ...primaryButtonProps },
  secondaryButton,
  leftButton,
}: ModalFooterProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const primaryButton = (() => {
    if (primaryButtonProps.to == null) {
      return (
        <ButtonWithLoadingState
          {...primaryButtonProps}
          loading={primaryButtonProps.loading ?? isLoading}
          onClick={async () => {
            setIsLoading(true);

            try {
              await primaryButtonProps.onClick();
            } finally {
              setIsLoading(false);
            }
          }}
          variant="primary"
          size="regular"
        >
          {primaryButtonText}
        </ButtonWithLoadingState>
      );
    }

    return (
      <ModalPrimitive.Button {...primaryButtonProps} variant="primary">
        {primaryButtonText}
      </ModalPrimitive.Button>
    );
  })();

  return (
    <ModalFooterContainer>
      <ModalPrimitive.Footer
        left={leftButton && <ModalButton {...leftButton} />}
        right={
          <Buttons>
            {secondaryButton && (
              <ModalButton {...secondaryButton} variant="secondary" />
            )}
            {primaryButton}
          </Buttons>
        }
      />
    </ModalFooterContainer>
  );
};

type BaseModalProps = {
  children?: React.ReactNode;
  width?: number;
  title: string;
  titleExtra?: React.ReactNode;
  description?: React.ReactNode;
  close?: () => void;
  variant?: 'action' | 'expanded';
  role?: 'dialog' | 'alertdialog';
};

export const BaseModal = ({
  children,
  width,
  title,
  titleExtra,
  description,
  close,
  variant,
  role,
}: BaseModalProps) => {
  const isFullscreen =
    useIsLayoutSize(LayoutSize.mobile) && variant === 'expanded';
  const titleId = useId();
  const descriptionId = useId();

  return (
    <ModalPrimitive.Root
      className={classNames(styles.modal, {
        [styles.expanded]: variant === 'expanded',
      })}
      width={width}
      fullscreen={isFullscreen}
      role={role}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
    >
      <ModalPrimitive.Header
        close={close}
        title={title}
        titleExtra={titleExtra}
        titleId={titleId}
      />
      {!!description && (
        <ModalPrimitive.Description
          id={descriptionId}
          className={styles.description}
        >
          {description}
        </ModalPrimitive.Description>
      )}
      <div className={styles.body}>{children}</div>
    </ModalPrimitive.Root>
  );
};

export type ModalProps = BaseModalProps &
  (
    | {
        variant?: 'action';
        footer: ModalFooterProps;
      }
    | {
        variant: 'expanded';
        footer?: never;
      }
  );

const Modal = ({ children, footer, ...props }: ModalProps) => (
  <BaseModal {...props}>
    {children}
    {footer && <ModalFooter {...footer} />}
  </BaseModal>
);

export default Modal;
